import type { jsPDF } from 'jspdf';

/**
 * Eingebettete Schrift fuer Rechnungen.
 *
 * Hintergrund: PDF/A — und damit ZUGFeRD — verlangt, dass jede benutzte Schrift
 * IM Dokument liegt. Die vierzehn Standardschriften von PDF (Helvetica, Times,
 * Courier) werden per Definition NICHT eingebettet, sondern nur referenziert.
 * Solange der Rechnungsgenerator sie benutzt, kann das Ergebnis kein gueltiges
 * PDF/A sein, gleich wie sorgfaeltig Metadaten und Anhang aufgebaut sind.
 *
 * Gewaehlt ist Liberation Sans:
 *   - SIL Open Font License 1.1, Einbettung und Weitergabe ausdruecklich erlaubt
 *   - METRISCH KOMPATIBEL zu Arial und damit praktisch auch zu Helvetica.
 *     Das ist der eigentliche Grund fuer diese Wahl: Zeichenbreiten und
 *     Zeilenumbrueche bleiben nahezu unveraendert, das Briefpapier verrutscht
 *     nicht, und ein Angebot neben einer Rechnung sieht weiterhin gleich aus.
 *   - liegt bereits im Projekt (Abhaengigkeit pdfjs-dist), es kommt also keine
 *     neue Bezugsquelle hinzu
 *
 * Die Schnitte werden unter dem Namen "helvetica" registriert. Das ist Absicht:
 * der Generator und das gemeinsame Briefpapier rufen an ueber vierzig Stellen
 * setFont('helvetica', …) auf. Ein Ueberschreiben des Namens ersetzt die
 * Schrift an allen Stellen auf einmal, ohne dass eine einzige davon angefasst
 * werden muss — und ohne das Angebots-PDF zu beruehren, das diese Funktion
 * schlicht nicht aufruft.
 */

interface Faces {
    regular: string;
    bold: string;
    italic: string;
}

/** Wurde in dieser jsPDF-Instanz schon registriert? */
const registered = new WeakSet<object>();

let cache: Promise<Faces> | null = null;
let loadedFaces: Faces | null = null;

/**
 * Laedt die Base64-Daten.
 *
 * Dynamisch importiert, weil die drei Schnitte zusammen rund 600 kB Base64
 * ergeben. Im Hauptbuendel waeren sie eine dauerhafte Last fuer jeden Aufruf
 * der Anwendung; so werden sie erst beim ersten Rechnungs-PDF geholt und
 * danach wiederverwendet.
 */
const loadFaces = (): Promise<Faces> => {
    if (!cache) {
        cache = Promise.all([
            import('./fonts/liberationSansRegular.js'),
            import('./fonts/liberationSansBold.js'),
            import('./fonts/liberationSansItalic.js'),
        ]).then(([r, b, i]) => ({
            regular: r.LIBERATION_SANS_REGULAR_BASE64,
            bold: b.LIBERATION_SANS_BOLD_BASE64,
            italic: i.LIBERATION_SANS_ITALIC_BASE64,
        }));
    }
    return cache;
};

/**
 * Laedt die Schriften und meldet, ob sie danach zur Verfuegung stehen.
 *
 * Kein Abbruch im Fehlerfall: die Rechnung entsteht dann mit Standardschriften
 * und taugt nur nicht als PDF/A. Der Fehler wird protokolliert, damit der
 * Unterschied nicht unbemerkt bleibt — ein Beleg ohne Text waere schlimmer als
 * einer, der kein PDF/A werden kann.
 */
export const ensureEmbeddedFont = async (): Promise<boolean> => {
    try {
        loadedFaces = await loadFaces();
        return true;
    } catch (e) {
        console.error(
            'Die eingebettete Schrift konnte nicht geladen werden. Das PDF entsteht mit Standardschriften und ist damit NICHT PDF/A-tauglich.',
            e,
        );
        return false;
    }
};

/**
 * Registriert die eingebetteten Schnitte in einem Dokument.
 *
 * Synchron, damit sie mitten im Rendervorgang benutzbar sind — der Aufrufer
 * muss vorher ensureEmbeddedFont abgewartet haben. Sind die Daten noch nicht
 * da, passiert nichts und die Funktion meldet false.
 */
export const registerEmbeddedFont = (doc: jsPDF): boolean => {
    if (registered.has(doc as unknown as object)) return true;
    if (!loadedFaces) return false;

    const d = doc as unknown as {
        addFileToVFS: (name: string, data: string) => void;
        addFont: (file: string, name: string, style: string) => void;
    };

    d.addFileToVFS('LiberationSans-Regular.ttf', loadedFaces.regular);
    d.addFont('LiberationSans-Regular.ttf', 'helvetica', 'normal');

    d.addFileToVFS('LiberationSans-Bold.ttf', loadedFaces.bold);
    d.addFont('LiberationSans-Bold.ttf', 'helvetica', 'bold');

    d.addFileToVFS('LiberationSans-Italic.ttf', loadedFaces.italic);
    d.addFont('LiberationSans-Italic.ttf', 'helvetica', 'italic');

    // jsPDF kennt fuer "bolditalic" keinen eigenen Eintrag; ohne diese Zeile
    // faellt es dort still auf die nicht eingebettete Standardschrift zurueck
    // und macht das ganze Dokument PDF/A-untauglich.
    d.addFont('LiberationSans-Bold.ttf', 'helvetica', 'bolditalic');

    registered.add(doc as unknown as object);
    return true;
};

/** Sind die Schriftdaten geladen? Fuer Tests und die PDF/A-Vorpruefung. */
export const isEmbeddedFontLoaded = (): boolean => loadedFaces !== null;

/** Nur fuer Tests: den geladenen Zustand zuruecksetzen. */
export const __resetEmbeddedFontForTests = (): void => {
    loadedFaces = null;
    cache = null;
};
